import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Button, CircularProgress, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import HealthIcon from '@mui/icons-material/MonitorHeart';
import BuildIcon from '@mui/icons-material/Build';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { apiService } from '../../services/apiService';

const MaintenanceTab = ({ observatoryId, frameType, onChanged }) => {
  // The 'all' sentinel (and empty) means "no frame-type filter".
  const effectiveFrameType = frameType && frameType !== 'all' ? frameType : undefined;
  const [jobsLoading, setJobsLoading] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [maintenanceRunning, setMaintenanceRunning] = useState(null);

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState('flat');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (!observatoryId) return;
    let cancelled = false;
    const loadHealth = async () => {
      setJobsLoading(true);
      try {
        const response = await apiService.getCalibrationHealth({
          observatory_id: observatoryId,
          ...(effectiveFrameType ? { frame_type: effectiveFrameType } : {}),
        });
        if (!cancelled) setHealthData(response.data || null);
      } catch (err) {
        if (!cancelled) console.error('Failed to load maintenance data:', err);
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    };
    loadHealth();
    return () => { cancelled = true; };
  }, [observatoryId, effectiveFrameType]);

  const refreshSummaryAfterChange = async () => {
    onChanged?.();
  };

  const runOp = async (key, op, successMsg, failMsg) => {
    setMaintenanceRunning(key);
    try {
      const result = await op();
      setNotification({ open: true, message: typeof successMsg === 'function' ? successMsg(result) : successMsg, severity: 'success' });
      await refreshSummaryAfterChange();
    } catch (err) {
      const detail = err.response?.data?.error || err.message || '';
      setNotification({ open: true, message: `${failMsg}: ${detail}`, severity: 'error' });
    } finally {
      setMaintenanceRunning(null);
    }
  };

  const handleExpiryCheck = () =>
    runOp('expire',
      () => apiService.triggerExpiryCheck(),
      (r) => `Expired ${r.data?.expired || 0} masters`,
      'Expiry check failed');

  const handleRetentionCleanup = () =>
    runOp('cleanup',
      () => apiService.triggerRetentionCleanup(),
      (r) => `Deleted ${r.data?.deleted || 0} frames`,
      'Cleanup failed');

  const handleClearCache = () =>
    runOp('cache',
      () => apiService.clearCalibrationCache(),
      'Calibrated FITS cache cleared',
      'Cache clear failed');

  const handleReleaseConsumed = () =>
    runOp('releaseConsumed',
      () => apiService.releaseConsumedFrames(observatoryId),
      (r) => `Released ${r.data?.released || 0} consumed frames back to active`,
      'Failed to release consumed frames');

  const handleSupersedeNullReadout = () =>
    runOp('supersedeNullReadout',
      () => apiService.supersedeNullReadoutMasters(observatoryId),
      (r) => `Superseded ${r.data?.superseded || 0} masters with missing readout mode`,
      'Failed to supersede null-readout masters');

  const handlePurgeNullReadout = () =>
    runOp('purgeNullReadout',
      () => apiService.purgeNullReadoutFrames(observatoryId),
      (r) => `Soft-deleted ${r.data?.deleted || 0} individual frames with missing readout mode`,
      'Failed to purge null-readout frames');

  const handleBulkDelete = async () => {
    if (!observatoryId || !bulkDeleteType) return;
    setBulkDeleting(true);
    try {
      const result = await apiService.bulkDeleteCalibrationFrames(observatoryId, bulkDeleteType);
      const deleted = result.data?.deleted || 0;
      const s3Errors = result.data?.s3_errors || 0;
      let msg = `Deleted ${deleted} individual ${bulkDeleteType} frames`;
      if (s3Errors > 0) msg += ` (${s3Errors} S3 cleanup errors)`;
      setNotification({ open: true, message: msg, severity: s3Errors > 0 ? 'warning' : 'success' });
      setBulkDeleteOpen(false);
      await refreshSummaryAfterChange();
    } catch (err) {
      setNotification({ open: true, message: `Bulk delete failed: ${err.response?.data?.error || err.message}`, severity: 'error' });
    } finally {
      setBulkDeleting(false);
    }
  };

  if (!observatoryId) {
    return <Alert severity="info">Select an observatory to view maintenance actions.</Alert>;
  }

  if (jobsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {healthData && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HealthIcon fontSize="small" /> System Health
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{healthData.masters?.active || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Active Masters</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" color={healthData.masters?.expiring_soon > 0 ? 'warning.main' : 'text.primary'}>
                  {healthData.masters?.expiring_soon || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">Expiring Soon</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{healthData.masters?.expired || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Expired</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5">{healthData.masters?.superseded || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Superseded</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Actions</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={maintenanceRunning === 'expire' ? <CircularProgress size={14} /> : <BuildIcon />}
            onClick={handleExpiryCheck}
            disabled={!!maintenanceRunning}
          >
            Run Expiry Check
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={maintenanceRunning === 'cleanup' ? <CircularProgress size={14} /> : <CleaningServicesIcon />}
            onClick={handleRetentionCleanup}
            disabled={!!maintenanceRunning}
          >
            Retention Cleanup
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={maintenanceRunning === 'cache' ? <CircularProgress size={14} /> : <DeleteIcon />}
            onClick={handleClearCache}
            disabled={!!maintenanceRunning}
          >
            Clear Cache
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={maintenanceRunning === 'releaseConsumed' ? <CircularProgress size={14} /> : <ReplayIcon />}
            onClick={handleReleaseConsumed}
            disabled={!!maintenanceRunning}
          >
            Release Consumed Frames
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={maintenanceRunning === 'supersedeNullReadout' ? <CircularProgress size={14} /> : <BuildIcon />}
            onClick={handleSupersedeNullReadout}
            disabled={!!maintenanceRunning}
          >
            Supersede Null-Readout Masters
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={maintenanceRunning === 'purgeNullReadout' ? <CircularProgress size={14} /> : <CleaningServicesIcon />}
            onClick={handlePurgeNullReadout}
            disabled={!!maintenanceRunning}
          >
            Purge Null-Readout Frames
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setBulkDeleteOpen(true)}
            disabled={!!maintenanceRunning}
          >
            Bulk Delete Frames
          </Button>
        </Box>
      </Box>

      <Dialog open={bulkDeleteOpen} onClose={() => !bulkDeleting && setBulkDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Delete Individual Frames</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Select a frame type to permanently delete ALL individual (non-master) frames of that type
            for the selected observatory.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Frame Type</InputLabel>
            <Select
              value={bulkDeleteType}
              onChange={(e) => setBulkDeleteType(e.target.value)}
              label="Frame Type"
            >
              <MenuItem value="flat">Flat</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="bias">Bias</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="error">
            This will permanently delete ALL individual {bulkDeleteType} frames from storage and the database.
            Master frames will NOT be affected. This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={14} /> : <DeleteIcon />}
          >
            {bulkDeleting ? 'Deleting...' : `Delete All Individual ${bulkDeleteType.charAt(0).toUpperCase() + bulkDeleteType.slice(1)} Frames`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {typeof notification.message === 'string' ? notification.message : String(notification.message ?? '')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaintenanceTab;
